import React from 'react';
import { Box, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { KPIBarProps } from '../../types/kpi.types';
import { KPICard } from './KPICard';

const Container = styled(Box)(({ theme }) => ({
  width: '100%',
  marginBottom: '32px',
  position: 'relative',
}));

const FlexRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'nowrap',
  gap: theme.spacing(1.5),
  width: '100%',
  overflowX: 'auto',
  '& > *': {
    flex: '1 1 0',
    minWidth: 0,
  },
}));

export type DashboardMode = 'light' | 'dark';

export const KPIBar: React.FC<
  KPIBarProps & {
    mode?: DashboardMode;
    selectedKpiId?: string;
    onKpiSelect?: (kpiId: string) => void;
    /** IDs de KPIs que soportan comparación de períodos (muestra indicador) */
    kpisWithComparison?: string[];
  }
> = ({
  kpis,
  loading = false,
  columns = { xs: 2, sm: 2, md: 4, lg: 4 },
  spacing = 2,
  mode = 'dark',
  selectedKpiId,
  onKpiSelect,
  kpisWithComparison,
}) => {
  const isSingleRow =
    columns.xs === columns.sm &&
    columns.sm === columns.md &&
    columns.md === columns.lg &&
    [5, 6, 7, 8].includes(columns.lg ?? 0);
  const isSelectable = Boolean(onKpiSelect);

  const useExtraCompact = isSingleRow && (columns.lg ?? 0) >= 8;
  const renderCard = (kpi: import('../../types/kpi.types').KPIConfig) => (
    <KPICard
      config={kpi}
      loading={loading}
      mode={mode}
      compact
      extraCompact={useExtraCompact}
      selected={isSelectable && selectedKpiId === kpi.id}
      onClick={isSelectable ? () => onKpiSelect?.(kpi.id) : undefined}
      showComparisonBadge={kpisWithComparison?.includes(kpi.id)}
    />
  );

  if (isSingleRow) {
    return (
      <Container>
        <FlexRow sx={{ alignItems: 'stretch', flexWrap: 'nowrap' }}>
          {kpis.map((kpi) => (
            <Box key={kpi.id} sx={{ minWidth: 0 }}>
              {renderCard(kpi)}
            </Box>
          ))}
        </FlexRow>
      </Container>
    );
  }

  return (
    <Container>
      <Grid container spacing={spacing}>
        {kpis.map((kpi) => (
          <Grid
            item
            xs={12 / (columns.xs || 2)}
            sm={12 / (columns.sm || 2)}
            md={12 / (columns.md || 4)}
            lg={12 / (columns.lg || 4)}
            key={kpi.id}
          >
            {renderCard(kpi)}
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};
